import { useCallback, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";

import { MandatoryFlagIcon, MilestoneDiamondIcon } from "../../assets";
import { CursorChangeProps, ResultType, SelectData } from "../../models";
import { searchWorkflowTasks, SearchWorkflowTasksRequest, SearchWorkflowTasksResponse } from "../../service/query";
import { ResultData } from "../../service/Shared";
import { flattenObject } from "../../utils";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridCursorDataLoadEventHandler,
  dataGridMapFilterCriteria,
} from "../../widget";
import { AvatarCellFormatter } from "../../widget/data/DataGrid/components/CellFormatters";
import { IconNoActionCellFormatterData } from "../../widget/data/DataGrid/models";
import { useDeepLinkedTask } from "../WorkflowTaskPanel";
import { useTaskTagsFilter } from "./useTaskTagsFilter";

// Extra column injected by a host (e.g. a landholding "Project" link column), with its insert position.
export interface TasksGeneralViewExtraColumn {
  index?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>;
}

export interface UseTasksGeneralViewConfig {
  /*
    The scoping criteria merged into every search, alongside the user's column filters. A general task
    hangs off its parent through objectType/objectUuid, so the scope is the parent's uuid on objectUuid.
    Undefined means the scope is not ready yet, so the grid returns no rows until it is.
  */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scopeFilterCriteria?: Record<string, any>;
  // Status filter passed as the second searchWorkflowTasks argument (e.g. nin [Complete]).
  statusFilter?: { operator: string; value: string[] };
  extraColumns?: TasksGeneralViewExtraColumn[];
  // Additional row wiring (e.g. a project link cell) applied to each formatted row.
  onFormatRow?: (row: ResultData, task: SearchWorkflowTasksResponse["results"][0]) => void;
  /*
    Whether this grid owns the `?taskUuid=` deep link. A host that shows this grid alongside another
    task view hands the link to whichever view is on screen, so the two do not resolve the same uuid
    twice. Required rather than defaulted: a host that leaves it unsaid is the case that reintroduces
    the duplicate resolution, so the answer is worth stating even when the grid stands alone (true).
  */
  deepLinkEnabled: boolean;
}

export interface UseTasksGeneralViewReturnData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  // False once a load completes with no rows (and no filter) — drives the empty state.
  hasData: boolean;
  onChange: DataGridCursorDataLoadEventHandler;

  refresh: boolean;
  handleRefresh: () => void;

  tagsFilterSelectData: SelectData;
  tagsFilterValues: string[];
  onTagsFilter: (keys: string[]) => void;

  /*
    Task panel: empty for "Add task", or carrying the row whose name was clicked. The panel derives the
    task type from the row itself (no step ⇒ general), so one panel serves both kinds of row.
  */
  showWorkflowTaskPanel: boolean;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  handleAddTask: () => void;
  handleCloseWorkflowTaskPanel: () => void;
}

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];

export const useTasksGeneralView = ({
  scopeFilterCriteria,
  statusFilter,
  extraColumns,
  onFormatRow,
  deepLinkEnabled,
}: UseTasksGeneralViewConfig): UseTasksGeneralViewReturnData => {
  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [hasData, setHasData] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();

  const handleRefresh = useCallback((): void => {
    setRefresh((prev) => !prev);
  }, []);

  const handleAddTask = useCallback((): void => {
    // No task ⇒ the panel opens in create mode.
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(true);
  }, []);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  /*
    A notification for a task links to the grid's host page naming the task in ?taskUuid= (KAN-9609), so
    the user lands on the task itself rather than having to find its row.
  */
  const deepLinkedTask = useDeepLinkedTask(deepLinkEnabled);
  useEffect(() => {
    if (!deepLinkedTask) return;
    setSelectedPanelTask(deepLinkedTask);
    setShowWorkflowTaskPanel(true);
  }, [deepLinkedTask]);

  // refresh doubles as the tag-filter refresh key: saving a task can create new org-level tags,
  // which have to appear in the filter alongside the reloaded grid.
  const { tagsFilterSelectData, tagsFilterValues, tagTaskUuids, onTagsFilter } = useTaskTagsFilter(
    handleRefresh,
    refresh
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      // Opens the task overlay for the clicked row; filtering and sorting still run on `name`.
      name: "Task",
      key: "nameLink",
      dataType: "string",
      formatter: "buttonLink",
      minWidth: 176,
      filterable: true,
      sortable: true,
    },
    {
      name: "Assignee",
      key: "avatar",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: true,
      minWidth: 112,
    },
    {
      // No minWidth — statuses are short, bounded values, so the grid's auto-size fits them exactly.
      key: "status",
      name: "Status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: true,
    },
    {
      // Date columns keep an explicit minWidth: auto-size measures the raw cell value, and for a Date
      // that is the 24-char ISO string (~230px), not the ~11 chars the formatter actually renders.
      key: "deadlineDate",
      name: "Deadline",
      dataType: "Date",
      formatter: "dateOnlyWithIndicator",
      minWidth: 148,
      filterable: false,
      sortable: true,
    },
    {
      key: "createdAt",
      name: "Created at",
      dataType: "Date",
      formatter: "dateOnly",
      minWidth: 148,
      filterable: false,
      sortable: true,
    },
    {
      name: "Updated by",
      key: "updatedByAvatar",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: false,
      minWidth: 112,
    },
    {
      // Icon columns keep an explicit minWidth for the same reason: only "custom" formatters are
      // excluded from auto-size measurement, so an iconNoAction cell measures as "[object Object]".
      name: "Milestone",
      key: "milestoneIcon",
      dataType: "string",
      formatter: "iconNoAction",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 112,
    },
    {
      name: "Mandatory",
      key: "mandatoryIcon",
      dataType: "string",
      formatter: "iconNoAction",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 112,
    },
  ];

  extraColumns?.forEach(({ column, index }) => {
    if (index === undefined) {
      columns.push(column);
    } else {
      columns.splice(index, 0, column);
    }
  });

  const formatData = useCallback(
    (responseData: SearchWorkflowTasksResponse | undefined): ResultData[] =>
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & {
          avatar: { fullName?: string };
          updatedByAvatar: { fullName?: string };
        };

        result.avatar = { fullName: d.assignee?.name };
        result.updatedByAvatar = { fullName: d.updatedByUser?.fullName ?? undefined };

        result.nameLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        result.milestoneIcon = d.isMilestone
          ? ({ icon: <MilestoneDiamondIcon width={20} height={20} /> } as IconNoActionCellFormatterData)
          : undefined;
        result.mandatoryIcon = d.isMandatory
          ? ({ icon: <MandatoryFlagIcon width={20} height={20} /> } as IconNoActionCellFormatterData)
          : undefined;

        onFormatRow?.(result, d);

        return result;
      }) || [],
    [onFormatRow]
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (scopeFilterCriteria === undefined) {
      return data;
    }

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    // The Task column renders a button link, so its filter and sort map back onto the task name.
    if (filterCriteria.nameLink) {
      filterCriteria.name = filterCriteria.nameLink;
      delete filterCriteria.nameLink;
    }

    if (filterCriteria.avatar) {
      filterCriteria.assignee = { name: filterCriteria.avatar };
      delete filterCriteria.avatar;
    }

    // Tag filter: restrict to the WorkflowTask uuids linked to the selected tags.
    if (tagTaskUuids !== undefined) {
      filterCriteria.uuid = { operator: "in", value: tagTaskUuids };
    }

    // Whether the user has narrowed the results themselves. A filtered-to-zero grid must not collapse
    // into the empty state, or the controls needed to clear the filter go with it. Measured before the
    // scope is merged in, since the scope is not something the user can clear.
    const isFiltered = Object.keys(filterCriteria).length > 0;

    Object.assign(filterCriteria, scopeFilterCriteria);

    const sortKeyMap: Record<string, string> = {
      nameLink: "name",
      avatar: "assignee.name",
      updatedByAvatar: "updatedByUser.fullName",
    };

    const request: SearchWorkflowTasksRequest = {
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    };

    await searchWorkflowTasks(request, statusFilter)
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setHasData(data.resultData.length > 0 || isFiltered);
        setDataIsLoading(false);
      });

    return data;
  };

  return {
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    hasData,
    onChange,

    refresh,
    handleRefresh,

    tagsFilterSelectData,
    tagsFilterValues,
    onTagsFilter,

    showWorkflowTaskPanel,
    selectedPanelTask,
    handleAddTask,
    handleCloseWorkflowTaskPanel,
  };
};
